import axios from "axios";
import { config } from "config/env";
import { AxiosInstance } from "axios";

const API_URL = config.api.baseUrl;

// Shared token refresh state management
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const createTokenRefreshInterceptor = (axiosInstance: AxiosInstance) => {
  return async (error: any) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, and not a login/refresh-token request
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/login") &&
      !originalRequest.url?.includes("/refresh-token")
    ) {
      if (isRefreshing) {
        // Queue the request to retry after token refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            // Retry with the same axios instance that made the original request
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
        const refreshToken = authUser?.refresh;

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Call refresh token endpoint using base axios (not the instance)
        const response = await axios.post(`${API_URL}/users/refresh-token`, {
          refresh: refreshToken,
        });

        const { access, refresh: newRefresh } = response.data.data;

        // Update tokens in localStorage
        authUser.access = access;
        authUser.refresh = newRefresh;
        localStorage.setItem("authUser", JSON.stringify(authUser));

        // Update default header for future requests
        originalRequest.headers.Authorization = `Bearer ${access}`;

        // Process queued requests
        processQueue(null, access);
        isRefreshing = false;

        // Retry the original request with the same axios instance
        return axiosInstance(originalRequest);
      } catch (refreshError: any) {
        // Refresh failed - clear queue and logout user
        processQueue(refreshError, null);
        isRefreshing = false;

        // Clear auth data
        localStorage.removeItem("authUser");
        localStorage.removeItem("userPermissions");

        // Redirect to login if not already there
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      }
    }

    // For other errors, just reject
    return Promise.reject(error);
  };
};

